<script setup lang="ts">
import { onMounted, h } from 'vue'
import { useRouter } from 'vue-router'
import {
  NCard, NButton, NSpace, NDataTable, NText, NEmpty, NAlert, NTag, type DataTableColumns,
} from 'naive-ui'
import { services, type ServiceOverview } from '../api'
import { useEvents } from '../composables/useEvents'
import { message } from '../message'
import { createServicesOverviewStore } from '../stores/servicesOverview'
import { toOperationView } from '../utils/operation'

const router = useRouter()
// Store delegates to services.overviewList().
const store = createServicesOverviewStore(services)
const { rows, loading } = store

async function load() {
  try {
    await store.load()
  } catch (e: any) {
    message.error(e?.message || 'load failed')
  }
}

useEvents({
  'deploy.success':     () => load(),
  'deploy.rolled_back': () => load(),
  'deploy.cancelled':   () => load(),
  'health.up':          () => load(),
  'health.down':        () => load(),
  'lifecycle.done':     () => load(),
})

onMounted(load)

const columns: DataTableColumns<ServiceOverview> = [
  {
    title: '', key: 'health', width: 28,
    render(row: ServiceOverview) {
      const c = row.health?.state === 'up' ? '#18a058'
        : row.health?.state === 'down' ? '#d03050' : '#bbb'
      return h('span', { style: `display:inline-block;width:10px;height:10px;border-radius:50%;background:${c}` })
    },
  },
  {
    title: 'Name', key: 'name',
    render(row: ServiceOverview) {
      const children = [
        h('a', {
          style: 'color:#2080f0;cursor:pointer;font-weight:500',
          onClick: (event: MouseEvent) => {
            event.stopPropagation()
            router.push(`/services/${row.service.id}`)
          },
        }, row.service.name),
      ]
      if (row.service.desc) {
        children.push(h(NText, {
          depth: 3,
          style: 'display:block;font-size:12px;line-height:1.4;margin-top:2px',
        }, () => row.service.desc))
      }
      return h('div', children)
    },
  },
  {
    title: 'Image', key: 'image_repo',
    render(row: ServiceOverview) { return row.service.image_repo },
  },
  {
    title: 'Domain', key: 'domain',
    render(row: ServiceOverview) { return row.service.domain || '—' },
  },
  {
    title: 'Version', key: 'current_version_id',
    render(row: ServiceOverview) { return row.service.current_version_id ? `#${row.service.current_version_id}` : '—' },
  },
  {
    title: 'Operation', key: 'operation',
    render(row: ServiceOverview) {
      if (!row.operation || row.operation.kind === 'idle' || row.operation.status === 'idle') return '—'
      const op = toOperationView(row.operation)
      return h(NTag, { type: op.tagType, size: 'small' }, () => op.statusText)
    },
  },
]

const rowProps = (row: ServiceOverview) => ({
  style: 'cursor: pointer',
  onClick: () => router.push(`/services/${row.service.id}`),
})
</script>

<template>
  <NAlert type="info" show-icon style="margin-bottom:16px">
    <template #header>服务列表 Services</template>
    所有受 sm 管理的服务。左侧圆点表示最近一次健康检查的结果（绿=up / 红=down / 灰=未知）。点击行进入详情页查看版本/部署历史/实时日志，以及执行 Start/Stop/Restart/Deploy 等操作。
  </NAlert>

  <NCard>
    <template #header>
      <NSpace justify="space-between" align="center" style="width: 100%">
        <NText>Services</NText>
        <NButton type="primary" size="small" @click="router.push('/services/new')" title="新建服务：仅注册元数据，不会启动容器">+ 新建服务 New service</NButton>
      </NSpace>
    </template>

    <NDataTable :columns="columns" :data="rows" :loading="loading" :bordered="false" :row-props="rowProps" />
    <NEmpty v-if="!loading && rows.length === 0" description="还没有服务。点击右上角「+ 新建服务」开始。" />
  </NCard>
</template>
